<template>
  <div v-if="deliveryItems.length" class="space-y-6 pb-[5px] text-[13px] font-light md:py-[5px]">
    <template v-for="item in deliveryItems">
      <div v-if="item" :key="item.text" class="flex items-center">
        <div class="mr-[15px]">
          <UiIcon
            :name="item.icon"
            class="text-primary"
            :style="{ width: item.size, height: item.size }"
          />
        </div>
        <div>
          <div v-if="item.code === 'truck'">
            <div
              v-html="item.text"
              class="inline"
            />,
            <button class="text-orange-80 hover:underline" @click.prevent="openChat">Need it sooner?</button>
          </div>
          <div v-else v-html="item.text"></div>
        </div>
      </div>
    </template>
  </div>
  <DialogsPurchaseProtection v-model="openPurchaseDialog" />
</template>

<script setup lang="ts">
import { ProductData, VariantResponse } from "ecom-types";
import { productCategoriesSKU, FREE_RETURNS_CATEGORY_SKUS } from "~/configs";

interface DeliveryItem {
  icon: string;
  text: string;
  size: string;
  code?: string;
}

const props = defineProps<{
  shipDate?: string;
  showFreeResize?: boolean;
  productCollections?: ProductData["collections"];
  showDeliveryInfoTruck?: boolean;
  onlyReturn?: boolean;
  currentConfiguration: VariantResponse | null;
  productInfo: ProductData;
  leadTime: number;
}>();

const { t } = useI18n();

const { openChat } = useZendesk();

const shipDate = computed(() => props.shipDate || "");

const { estimateShipping } = useEstimateShipping(
  shipDate,
  "EEEE, LLLL dd",
);

const openPurchaseDialog = ref(false);
const purchaseProtectionBtn = ref<Element | null>(null);

const hasReadyToShipCategory = computed(() => {
  return props.productInfo.categories?.some(
    (category) => category.sku === productCategoriesSKU.readyToShip,
  );
});

const hasSaleCategory = computed(() => {
  return props.productInfo.categories?.some(
    (category) => category.sku === productCategoriesSKU.sale,
  );
});

const hasFreeReturnsByCategory = computed(() => {
  const categories = props.productInfo.categories;
  if (!categories) return false;
  const hasTargetCategory = categories.some((category) =>
    FREE_RETURNS_CATEGORY_SKUS.includes(category.sku),
  );
  const hasCCCategory = categories.some(
    (category) => category.sku === productCategoriesSKU.sale,
  );
  return hasTargetCategory && !hasCCCategory;
});

const deliveryItems = computed(() => {
  const result: DeliveryItem[] = [];
  if (props.showDeliveryInfoTruck) {
    let deliveryIcon = "truck";
    let deliveryText = t("truckText", {
      estimateShipping: estimateShipping.value,
    });
    let deliveryIconSize = "28px";
    const isFastShipping = props.leadTime && props.leadTime <= 2;

    if (hasReadyToShipCategory.value) {
      deliveryIcon = "truck-red";
      // deliveryText = t("truckTextShipsNextBusinessDayWithReturns");
      deliveryText = t("truckTextShipsNextBusinessDay");
      deliveryIconSize = "60px";
    } else if (hasSaleCategory.value) {
      deliveryIcon = "truck-red";
      deliveryText = t("truckTextShipsNextBusinessDay");
      deliveryIconSize = "60px";
    } else if (
      !hasReadyToShipCategory.value &&
      !hasSaleCategory.value &&
      isFastShipping
    ) {
      deliveryIcon = "truck-red";
      // deliveryText = t("truckTextShipsNextBusinessDayWithReturns");
      deliveryText = t("truckTextShipsNextBusinessDay");
      deliveryIconSize = "60px";
    } else if (props.showFreeResize) {
      // deliveryText = t("truckTextWithReturns", {
      //   estimateShipping: estimateShipping.value,
      // });
      deliveryText = t("truckText", {
        estimateShipping: estimateShipping.value,
      });
    }

    result.push({
      icon: deliveryIcon,
      text: deliveryText,
      size: deliveryIconSize,
      code: "truck",
    });
  }

  if (props.showDeliveryInfoTruck && (hasReadyToShipCategory.value || props.showFreeResize || hasFreeReturnsByCategory.value)) {
    result.push({
      icon: "return",
      text: t("free30DayReturns"),
      size: "28px",
    });
  }

  if (props.showFreeResize && !props.onlyReturn) {
    result.push({
      icon: "review_ring",
      text: t("labelText"),
      size: "28px",
    });
  }
  return result;
});

function purchaseProtectionOpen() {
  openPurchaseDialog.value = true;
}

onMounted(() => {
  purchaseProtectionBtn.value = document.querySelector(
    "[data-purchase-protection]",
  );
  if (purchaseProtectionBtn.value) {
    purchaseProtectionBtn.value.addEventListener(
      "click",
      purchaseProtectionOpen,
    );
  }
});

onBeforeUnmount(() => {
  if (purchaseProtectionBtn.value) {
    purchaseProtectionBtn.value.removeEventListener(
      "click",
      purchaseProtectionOpen,
    );
  }
});
</script>
